import { useRef, useState } from "react";
import { ImagePlus, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, BACKEND_URL } from "../../lib/api";
import { useGet } from "../../lib/hooks";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Pill } from "../Common";
import { WIDGETS } from "../dashboard/Widgets";

const STATUS = { requested: ["amber", "Requested"], in_progress: ["blue", "Being built"], built: ["green", "Built – in Widget Library as Draft"] };

function NewRequest({ onSaved, defs }) {
  const [f, setF] = useState({ title: "", instructions: "", kind: "new", widget_type: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const pick = useRef(null);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/widget-requests", f);
      for (const file of files) { const fd = new FormData(); fd.append("file", file); await api.post(`/widget-requests/${data.id}/screenshots`, fd); }
      toast.success("Widget request saved. Tell the builder in chat when you'd like it built"); setF({ title: "", instructions: "", kind: "new", widget_type: "" }); setFiles([]); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-[#C2A462]/30 bg-[#0f1a2b] p-5 space-y-3" data-testid="widget-request-form">
      <div className="flex flex-wrap gap-2">{[["new", "New widget"], ["change", "Change an existing widget"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} data-testid={`widget-request-kind-${k}`}
          className={`rounded-full border px-3 py-1 text-xs transition-colors ${f.kind === k ? "border-[#C2A462] bg-[#C2A462]/20 text-[#EBD6A0]" : "border-white/15 text-slate-400 hover:border-[#C2A462]/50"}`}>{l}</button>))}</div>
      {f.kind === "change" && <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={f.widget_type} data-testid="widget-request-widget"
        onChange={(e) => { const lbl = e.target.selectedOptions[0]?.text || ""; setF({ ...f, widget_type: e.target.value, title: f.title || `Change: ${lbl}` }); }}>
        <option value="">Choose the widget to change…</option>
        {Object.entries(WIDGETS).filter(([k]) => k !== "library").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        {(defs || []).map((d) => <option key={d.id} value={`def:${d.id}`}>{d.name} (generated)</option>)}
      </select>}
      <Input placeholder={f.kind === "change" ? "Request name, e.g. Smaller Key Dates" : "Widget name, e.g. Staff training compliance"} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="widget-request-title" />
      <Textarea rows={6} placeholder={f.kind === "change" ? "What should change? e.g. make it smaller, show this week only…" : "What should it show, where does the data come from, who is it for, how should it look?"} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} data-testid="widget-request-instructions" />
      <div className="flex flex-wrap items-center gap-2">
        <input ref={pick} type="file" multiple accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => setFiles([...files, ...e.target.files])} data-testid="widget-request-files" />
        <Button size="sm" variant="secondary" onClick={() => pick.current?.click()} data-testid="widget-request-add-screenshot"><ImagePlus className="h-3.5 w-3.5 mr-1" />Attach screenshots</Button>
        {files.map((x, i) => <span key={i} className="text-xs text-slate-400">{x.name}</span>)}
        <Button size="sm" className="ml-auto" disabled={busy || !f.title.trim()} onClick={submit} data-testid="widget-request-submit"><Send className="h-3.5 w-3.5 mr-1" />{busy ? "Saving…" : "Save request"}</Button>
      </div>
    </div>
  );
}

function RequestCard({ r, reload }) {
  const del = async () => { if (!window.confirm(`Delete request "${r.title}"?`)) return; try { await api.delete(`/widget-requests/${r.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const [tone, label] = STATUS[r.status] || STATUS.requested;
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-4 space-y-2" data-testid={`widget-request-${r.id}`}>
      <div className="flex items-center gap-2"><div className="text-sm font-semibold text-slate-100 flex-1">{r.title}</div>{r.kind === "change" && <Pill tone="blue">Change</Pill>}<Pill tone={tone}>{label}</Pill>
        <button onClick={del} data-testid={`widget-request-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button></div>
      {r.instructions && <p className="text-xs text-slate-400 whitespace-pre-wrap">{r.instructions}</p>}
      {!!r.screenshots?.length && <div className="flex flex-wrap gap-2">{r.screenshots.map((s) => (
        <a key={s.id} href={`${BACKEND_URL}/api/widget-requests/${r.id}/screenshots/${s.id}`} target="_blank" rel="noreferrer">
          <img src={`${BACKEND_URL}/api/widget-requests/${r.id}/screenshots/${s.id}`} alt={s.name} className="h-20 w-auto rounded border border-white/10 object-cover" /></a>))}</div>}
    </div>
  );
}

export function WidgetRequests() {
  const [list, reload] = useGet("/widget-requests");
  const [defs] = useGet("/widget-defs");
  return (
    <div className="space-y-4" data-testid="widget-requests">
      <div className="rounded-md border border-[#C2A462]/25 bg-[#16213a] p-4 text-sm text-slate-300 space-y-1" data-testid="widget-requests-howto">
        <div className="font-semibold text-[#EBD6A0]">How the Widget Creator works</div>
        <div>1. Choose <b>New widget</b> or <b>Change an existing widget</b>, describe it and attach screenshots, then click Save request.</div>
        <div>2. Tell the builder in chat, e.g. "build my Smaller Key Dates request". Requests are not built automatically.</div>
        <div>3. Once built, it appears in the Widget Library (new widgets start as a Draft). Finalise and share it from Built widgets below.</div>
      </div>
      <NewRequest onSaved={reload} defs={defs} />
      {(list || []).map((r) => <RequestCard key={r.id} r={r} reload={reload} />)}
    </div>
  );
}
